import { ApolloError, gql, useApolloClient, useQuery } from '@apollo/client'
import {
    useAuthorizationContext,
    useIsLoggedIn,
} from '@emico-hooks/login-token'
import { useCallback, useEffect } from 'react'

import { getSentry } from '@emico/utils'

import { Product } from './ProductPage/GetProduct.query'
import { ConfigurableProduct } from '../graphql/schema.generated'

const getRecentlyViewedProducts = gql`
    query GetRecentlyViewedProducts {
        customer {
            recentlyViewed {
                products {
                    id
                    sku
                }
            }
        }
    }
`

const trackProdViewMutation = gql`
    mutation TrackProductView($sku: String!) {
        trackProductView(sku: $sku) {
            result
        }
    }
`

export type LastViewedProductsType = Array<
    Pick<ConfigurableProduct | Product, 'id' | 'sku'>
>

type LastViewed = {
    customer: {
        recentlyViewed: {
            products: LastViewedProductsType
        }
    }
}

export const useLastViewedProducts = () => {
    const context = useAuthorizationContext()
    const isLoggedIn = useIsLoggedIn()

    const { data, loading, error } = useQuery<LastViewed>(
        getRecentlyViewedProducts,
        {
            skip: !isLoggedIn,
            context,
        },
    )

    const err = error
        ? error instanceof ApolloError
            ? error.graphQLErrors?.[0]?.message
            : (error as any)?.message
        : undefined

    return {
        products: data?.customer?.recentlyViewed?.products || [],
        loading,
        error: err,
    }
}

export const useTrackProductView = (sku?: string) => {
    const client = useApolloClient()
    const context = useAuthorizationContext()
    const isLoggedIn = useIsLoggedIn()

    const trackProduct = useCallback(
        async (productSku: string) => {
            if (!isLoggedIn) {
                return
            }
            if (!productSku) {
                return
            }
            try {
                await client.mutate<{ result: boolean }>({
                    mutation: trackProdViewMutation,
                    context,
                    variables: { sku: productSku },
                    refetchQueries: [
                        { query: getRecentlyViewedProducts, context },
                    ],
                })
            } catch (err) {
                // No need to inform user, just hand it over to Sentry
                getSentry()?.captureException(err)
            }
        },
        [client, context, isLoggedIn],
    )

    useEffect(() => {
        ;(async (productSku: string) => {
            if (!productSku) {
                return
            }

            await trackProduct(productSku)
        })(sku ?? '')
    }, [sku, trackProduct])
}
